import { Card, Nav, Tab } from "react-bootstrap";
import { getMenuLanguages } from "../../../constants/menuLanguages";

import PageTitle from "../../layouts/PageTitle";
import { useEffect, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import config from "../../../../config.json";
import "../../../i18n";
import { useTranslation } from "react-i18next";
import {
  IMAGE_PICKER_ACCEPT,
  validateImageFile,
} from "../../../utils/imageValidation";
import MenuCustomerDisplaySwitches from "./MenuCustomerDisplaySwitches";
import ProductTitleIconStylePicker from "./ProductTitleIconStylePicker";
import ProductCardSizePicker from "./ProductCardSizePicker";
import { PRODUCT_TITLE_ICON_STYLES } from "../../../constants/productTitleIconStyles";
import { normalizeProductCardSize } from "../../../constants/productCardSizes";
import MenuTextFontPicker from "./MenuTextFontPicker";
import MenuTitlesAlignPickers from "./MenuTitlesAlignPickers";
import MenuSummaryAlignPicker from "./MenuSummaryAlignPicker";
import MenuSummaryBulletPicker from "./MenuSummaryBulletPicker";
import MenuSummaryPlusPicker from "./MenuSummaryPlusPicker";
import MenuFormDisplaySection from "./MenuFormDisplaySection";
import MenuContentTypeSection from "./MenuContentTypeSection";
import { normalizeMenuContentType, MENU_CONTENT_TYPES } from "../../../constants/menuContentType";
import MenuTree from "./MenuTree/MenuTree";
import { normalizeMenuTextFont } from "../../../constants/menuTextFonts";
import { normalizeSummaryTextAlign } from "../../../constants/summaryTextAlign";
import { normalizeSummaryBulletStyle } from "../../../constants/summaryBulletStyle";
import {
  loadCategoryTitleAlign,
  loadSubcategoryTitleAlign,
  loadProductTitleAlign,
} from "../../../constants/titleAlign";
import "./MenuFormSidebar.css";
import "./MenuFormShell.css";

const EditMenu = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [imageUrls, setImageUrls] = useState(null);
  const [staticMenuFile, setStaticMenuFile] = useState(null);
  const [staticFilePreview, setStaticFilePreview] = useState(null);
  const [pendingStaticFile, setPendingStaticFile] = useState(null);

  const languages = getMenuLanguages(t);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    const { isValid, message } = validateImageFile(selectedFile, 5);
    if (!isValid) {
      swal("Warning", message, "warning");
      event.target.value = "";
      return;
    }

    const updatedFile = selectedFile;
    const updatedUrl = selectedFile ? URL.createObjectURL(selectedFile) : null;

    setFile(updatedFile);
    setImageUrls(updatedUrl);
  };

  const handleRemoveImage = () => {
    setGlobalDetails((prevDetails) => ({
      ...prevDetails,
      photo: null, // On vide la liste des photos
    }));

    setImageUrls(null); // Supprimer l'URL prévisualisée
    setFile(null); // Supprimer le fichier
  };

  const [menuDetails, setMenuDetails] = useState(
    languages.reduce((acc, lang) => {
      acc[lang.code] = {
        menuName: "",
        menuPriceCaption: "",
      };
      return acc;
    }, {})
  );

  const [globalDetails, setGlobalDetails] = useState({
    photo: null,
    productImagesDisabled: false,
    productPricesHidden: false,
    searchBarHidden: false,
    summaryButtonEnabled: false,
    summaryTextAlign: "left",
    summaryShowPlusBetweenCategories: true,
    summaryBulletStyle: "utensils",
    productTitleIconStyle: "none",
    productCardSize: "normal",
    productTextFont: "roboto",
    categoryTextFont: "roboto",
    subCategoryTextFont: "roboto",
    categoryTitleAlign: "center",
    subCategoryTitleAlign: "left",
    productTitleAlign: "left",
    menuPrice: "",
    menuContentType: MENU_CONTENT_TYPES.INTERACTIVE,
    staticMenuMediaType: null,
  });

  const [activeLang, setActiveLang] = useState(languages[0].code);
  const [mainSectionTab, setMainSectionTab] = useState("texts");
  const isInteractiveMenu =
    globalDetails.menuContentType === MENU_CONTENT_TYPES.INTERACTIVE;

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "manage" || tab === "display" || tab === "texts") {
      setMainSectionTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await axios.get(`${config.dev}/V2/menu/getById/${id}`);
        const menu = response.data;

        // Préparer les données langues
        const details = {};
        languages.forEach((lang) => {
          const langData = menu.menuDetails.find(
            (d) => d.language === lang.code
          );
          details[lang.code] = {
            menuName: langData?.menuName || "",
            menuPriceCaption: langData?.menuPriceCaption || "",
          };
        });
        setMenuDetails(details);

        // Photo
        setGlobalDetails({
          photo: menu.photo || null,
          productImagesDisabled: Boolean(menu.productImagesDisabled),
          productPricesHidden: Boolean(menu.productPricesHidden),
          searchBarHidden: Boolean(menu.searchBarHidden),
          summaryButtonEnabled: Boolean(menu.summaryButtonEnabled),
          summaryTextAlign: normalizeSummaryTextAlign(menu.summaryTextAlign),
          summaryShowPlusBetweenCategories:
            menu.summaryShowPlusBetweenCategories !== false,
          summaryBulletStyle: normalizeSummaryBulletStyle(menu.summaryBulletStyle),
          productTitleIconStyle: PRODUCT_TITLE_ICON_STYLES.includes(
            menu.productTitleIconStyle
          )
            ? menu.productTitleIconStyle
            : "none",
          productCardSize: normalizeProductCardSize(menu.productCardSize),
          productTextFont: normalizeMenuTextFont(menu.productTextFont),
          categoryTextFont: normalizeMenuTextFont(menu.categoryTextFont),
          subCategoryTextFont: normalizeMenuTextFont(menu.subCategoryTextFont),
          categoryTitleAlign: loadCategoryTitleAlign(menu),
          subCategoryTitleAlign: loadSubcategoryTitleAlign(menu),
          productTitleAlign: loadProductTitleAlign(menu),
          menuPrice:
            menu.menuPrice != null && menu.menuPrice !== ""
              ? menu.menuPrice
              : "",
          menuContentType: normalizeMenuContentType(menu.menuContentType),
          staticMenuMediaType: menu.staticMenuMediaType || null,
        });
        setStaticMenuFile(menu.staticMenuFile || null);
        setStaticFilePreview(null);
        setPendingStaticFile(null);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching menu:", error);
        setLoading(false);
      }
    };

    fetchMenu();
  }, [id]);

  const handleLangInputChange = (lang, field, value) => {
    setMenuDetails((prevDetails) => ({
      ...prevDetails,
      [lang]: { ...prevDetails[lang], [field]: value },
    }));
  };

  const handleStaticFileSelect = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const isPdf =
      selectedFile.type === "application/pdf" ||
      selectedFile.name.toLowerCase().endsWith(".pdf");
    const isImage = ["image/webp", "image/jpeg", "image/png"].includes(
      selectedFile.type
    );

    if (!isPdf && !isImage) {
      swal(t("warning"), t("menuStaticFileInvalid"), "warning");
      event.target.value = "";
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      swal(t("warning"), t("menuStaticFileTooLarge"), "warning");
      event.target.value = "";
      return;
    }

    if (staticFilePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(staticFilePreview);
    }

    setPendingStaticFile(selectedFile);
    setStaticFilePreview(URL.createObjectURL(selectedFile));
    setGlobalDetails((prev) => ({
      ...prev,
      menuContentType: MENU_CONTENT_TYPES.STATIC,
      staticMenuMediaType: isPdf ? "pdf" : "image",
    }));
    event.target.value = "";
  };

  const handleRemoveStaticFile = () => {
    if (staticFilePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(staticFilePreview);
    }
    setPendingStaticFile(null);
    setStaticFilePreview(null);
    setStaticMenuFile(null);
    setGlobalDetails((prev) => ({
      ...prev,
      staticMenuMediaType: null,
    }));
  };

  const handleSubmitStaticFile = async () => {
    if (!pendingStaticFile) return;
    const token = localStorage.getItem("tokenLogin");
    const formData = new FormData();
    formData.append("file", pendingStaticFile);
    const response = await axios.put(
      `${config.dev}/v2/menu/static-file/${id}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    setStaticMenuFile(response.data?.staticMenuFile || null);
    setPendingStaticFile(null);
    if (staticFilePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(staticFilePreview);
    }
    setStaticFilePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const menuDetailsToSend = Object.entries(menuDetails).map(
      ([lang, details]) => ({
        language: lang,
        menuName: details.menuName?.trim(),
        menuPriceCaption: details.menuPriceCaption?.trim() || "",
      })
    );
    if (menuDetailsToSend.length > 0) {
      const hasTitle = menuDetailsToSend.some((detail) => detail.menuName);
      if (!hasTitle) {
        swal("WARNING", t("menuFormTitleRequired"), "warning");
      } else {
        if (
          globalDetails.menuContentType === MENU_CONTENT_TYPES.STATIC &&
          !staticMenuFile &&
          !pendingStaticFile
        ) {
          swal(t("warning"), t("menuStaticFileRequired"), "warning");
          return;
        }

        const rawPrice =
          globalDetails.menuPrice === "" ||
          globalDetails.menuPrice === null ||
          globalDetails.menuPrice === undefined
            ? null
            : Number(String(globalDetails.menuPrice).replace(",", "."));
        const safeMenuPrice =
          rawPrice !== null && Number.isFinite(rawPrice) && rawPrice >= 0
            ? rawPrice
            : null;

        try {
          const response = await axios.put(
            `${config.dev}/v2/menu/update/${id}`,
            {
              menuDetails: menuDetailsToSend,
              photo: globalDetails.photo,
              productImagesDisabled: globalDetails.productImagesDisabled,
              productPricesHidden: globalDetails.productPricesHidden,
              searchBarHidden: globalDetails.searchBarHidden,
              summaryButtonEnabled: globalDetails.summaryButtonEnabled,
              summaryTextAlign: globalDetails.summaryTextAlign,
              summaryShowPlusBetweenCategories:
                globalDetails.summaryShowPlusBetweenCategories,
              summaryBulletStyle: globalDetails.summaryBulletStyle,
              productTitleIconStyle: globalDetails.productTitleIconStyle,
              productCardSize: globalDetails.productCardSize,
              productTextFont: globalDetails.productTextFont,
              categoryTextFont: globalDetails.categoryTextFont,
              subCategoryTextFont: globalDetails.subCategoryTextFont,
              categoryTitleAlign: globalDetails.categoryTitleAlign,
              subCategoryTitleAlign: globalDetails.subCategoryTitleAlign,
              productTitleAlign: globalDetails.productTitleAlign,
              menuPrice: safeMenuPrice,
              menuContentType: globalDetails.menuContentType,
              // Interactive: send null so API $unsets (null is not a valid enum value)
              staticMenuMediaType:
                globalDetails.menuContentType === MENU_CONTENT_TYPES.STATIC
                  ? globalDetails.staticMenuMediaType
                  : null,
              staticMenuFile:
                globalDetails.menuContentType === MENU_CONTENT_TYPES.STATIC
                  ? staticMenuFile
                  : null,
            }
          );

          if (response) {
            if (pendingStaticFile) {
              await handleSubmitStaticFile();
            }
            swal("SUCCESS", t("menuUpdatedSuccess"), "success");
          }
        } catch (error) {
          if (
            error.response &&
            error.response.data &&
            error.response.data.message
          ) {
            swal("ERROR", error.response.data.message, "error");
          } else {
            swal("ERROR", t("unexpectedError"), "error");
          }
        }
      }
    }

    if (file) {
      console.log("to update photo");

      handleSubmitPhotos();
    }
  };

  const handleSubmitPhotos = async () => {
    // e.preventDefault();
    const formDataToSend = new FormData();

    if (file) formDataToSend.append("file", file);

    try {
      const response = await axios.put(
        `${config.dev}/v2/menu/photos/${id}`,
        formDataToSend
      );

      swal("SUCCESS", t("menuUpdatedSuccess"), "success");
      // navigate("vite/demo/menu");

      setGlobalDetails((prevDetails) => ({
        ...prevDetails,
        photo: response.data.photo,
      }));
    } catch (error) {
      console.error(
        "Error updating product:",
        error.response?.data || error.message
      );
      swal("ERROR", t("failedUpdateMenuPhoto"), "error");
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t("loading")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid menu-form-page px-3 pb-5">
      <PageTitle activeMenu={t("editMenu")} motherMenu={t("kitchen")} />

      <div className="row g-4">
        {mainSectionTab !== "manage" && (
        <div className="col-lg-4 col-xl-3">
          <Card className="menu-form-sidebar-card h-100">
            <Card.Header className="menu-form-sidebar-photo-header border-0 bg-white">
              {t("menuPhoto")}
            </Card.Header>
            <Card.Body className="menu-form-sidebar-photo pt-0">
              <div className="d-flex flex-column align-items-center">
                {globalDetails?.photo || imageUrls ? (
                  <div
                    className="position-relative"
                    style={{ width: "160px", height: "160px" }}
                  >
                    <img
                      src={
                        imageUrls ||
                        `${config.UPLOADS_SERVER}/menu/${globalDetails?.photo}`
                      }
                      alt=""
                      style={{
                        width: "160px",
                        height: "160px",
                        objectFit: "cover",
                        borderRadius: "16px",
                        border: "2px solid #FA8072",
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage()}
                      className="btn btn-danger position-absolute top-50 start-50 translate-middle rounded-circle p-0 d-flex align-items-center justify-content-center"
                      style={{
                        width: "40px",
                        height: "40px",
                        zIndex: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                      }}
                      title={t("deleteImage")}
                    >
                      <i className="fa fa-trash" style={{ fontSize: "15px" }} />
                    </button>
                  </div>
                ) : (
                  <label
                    className="btn btn-outline-secondary btn-sm"
                    style={{ cursor: "pointer" }}
                  >
                    <i className="fa fa-camera me-1" />
                    {t("uploadPhoto")}
                    <input
                      hidden
                      type="file"
                      accept={IMAGE_PICKER_ACCEPT}
                      onChange={(e) => handleFileChange(e)}
                    />
                  </label>
                )}
              </div>
            </Card.Body>
          </Card>
        </div>
        )}

        <div className={mainSectionTab === "manage" ? "col-12" : "col-lg-8 col-xl-9"}>
          <Card className="menu-form-shell">
            <Card.Body>
              {mainSectionTab === "texts" ? (
                <>
                  <div className="menu-form-shell__title">{t("menuFormMainHeading")}</div>
                  <p className="menu-form-shell__subtitle">{t("menuFormMainSub")}</p>
                </>
              ) : mainSectionTab === "manage" ? (
                <>
                  <div className="menu-form-shell__title">{t("menuFormManageHeading")}</div>
                  <p className="menu-form-shell__subtitle">{t("menuFormManageSub")}</p>
                </>
              ) : (
                <>
                  <div className="menu-form-shell__title">{t("menuFormDisplayHeading")}</div>
                  <p className="menu-form-shell__subtitle">{t("menuFormDisplaySub")}</p>
                </>
              )}

              <Tab.Container
                activeKey={mainSectionTab}
                onSelect={(k) => k && setMainSectionTab(k)}
              >
                <Nav variant="tabs" className="menu-form-main-nav mb-3">
                  <Nav.Item>
                    <Nav.Link eventKey="texts">{t("menuFormTabTexts")}</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="display">{t("menuFormTabDisplay")}</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="manage">{t("menuFormTabManage")}</Nav.Link>
                  </Nav.Item>
                </Nav>
                <Tab.Content>
                  <Tab.Pane eventKey="texts">
                    <MenuContentTypeSection
                      menuContentType={globalDetails.menuContentType}
                      staticMenuMediaType={globalDetails.staticMenuMediaType}
                      staticMenuFile={staticMenuFile}
                      staticFilePreview={staticFilePreview}
                      onMenuContentTypeChange={(type) =>
                        setGlobalDetails((prev) => ({
                          ...prev,
                          menuContentType: type,
                        }))
                      }
                      onStaticFileSelect={handleStaticFileSelect}
                      onRemoveStaticFile={handleRemoveStaticFile}
                    />
                    <Tab.Container
                      activeKey={activeLang}
                      onSelect={(k) => k && setActiveLang(k)}
                    >
                      <Nav variant="pills" className="menu-form-lang-nav mb-3">
                        {languages.map((lang) => (
                          <Nav.Item key={lang.code}>
                            <Nav.Link eventKey={lang.code}>
                              <img width={22} height={16} src={lang.flag} alt="" className="me-1" />
                              {lang.label}
                            </Nav.Link>
                          </Nav.Item>
                        ))}
                      </Nav>
                      <Tab.Content>
                        {languages.map((lang) => (
                          <Tab.Pane eventKey={lang.code} key={lang.code}>
                            <div className="mb-3">
                              <div className="menu-form-field-label">{t("title")}</div>
                              <input
                                type="text"
                                className={`form-control form-control-lg ${
                                  lang.code === "ar" ? "text-end" : ""
                                }`}
                                value={menuDetails[lang.code].menuName}
                                onChange={(e) =>
                                  handleLangInputChange(
                                    lang.code,
                                    "menuName",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <div className="mb-0">
                              <div className="menu-form-field-label">
                                {t("menuPriceCaptionShort")}
                              </div>
                              <p className="menu-form-field-hint mb-2">
                                {t("menuPriceCaptionSectionHint")}
                              </p>
                              <input
                                type="text"
                                className={`form-control ${
                                  lang.code === "ar" ? "text-end" : ""
                                }`}
                                placeholder={t("menuPriceCaptionPlaceholder")}
                                value={menuDetails[lang.code].menuPriceCaption || ""}
                                onChange={(e) =>
                                  handleLangInputChange(
                                    lang.code,
                                    "menuPriceCaption",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Tab.Pane>
                        ))}
                      </Tab.Content>
                    </Tab.Container>

                    <div className="menu-form-optional-price">
                      <div className="menu-form-field-label">
                        {t("menuPriceLabel")}{" "}
                        <span className="text-muted fw-normal small">
                          ({t("menuPriceOptionalHint")})
                        </span>
                      </div>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        step="0.01"
                        placeholder={t("menuPricePlaceholder")}
                        value={globalDetails.menuPrice}
                        onChange={(e) => {
                          const v = e.target.value.replace(",", ".");
                          if (
                            v === "" ||
                            (!Number.isNaN(Number(v)) && Number(v) >= 0)
                          ) {
                            setGlobalDetails((prev) => ({
                              ...prev,
                              menuPrice: v,
                            }));
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e") e.preventDefault();
                        }}
                      />
                    </div>
                  </Tab.Pane>
                  <Tab.Pane eventKey="display">
                    <div className="menu-form-display-tab">
                      <MenuFormDisplaySection
                        step={1}
                        title={t("menuFormDisplaySectionVisibility")}
                        hint={t("menuFormDisplaySectionVisibilityHint")}
                      >
                        <MenuCustomerDisplaySwitches
                          imagesShown={!globalDetails.productImagesDisabled}
                          pricesShown={!globalDetails.productPricesHidden}
                          searchShown={!globalDetails.searchBarHidden}
                          summaryEnabled={globalDetails.summaryButtonEnabled}
                          onImagesShownChange={(shown) =>
                            setGlobalDetails((prev) => ({
                              ...prev,
                              productImagesDisabled: !shown,
                            }))
                          }
                          onPricesShownChange={(shown) =>
                            setGlobalDetails((prev) => ({
                              ...prev,
                              productPricesHidden: !shown,
                            }))
                          }
                          onSearchShownChange={(shown) =>
                            setGlobalDetails((prev) => ({
                              ...prev,
                              searchBarHidden: !shown,
                            }))
                          }
                          onSummaryEnabledChange={(enabled) =>
                            setGlobalDetails((prev) => ({
                              ...prev,
                              summaryButtonEnabled: enabled,
                            }))
                          }
                          idPrefix="edit-menu"
                        />
                      </MenuFormDisplaySection>

                      <MenuFormDisplaySection
                        step={2}
                        title={t("menuFormDisplaySectionSummary")}
                        hint={t("menuFormDisplaySectionSummaryHint")}
                      >
                        <MenuSummaryAlignPicker
                          value={globalDetails.summaryTextAlign}
                          onChange={(align) =>
                            setGlobalDetails((prev) => ({
                              ...prev,
                              summaryTextAlign: align,
                            }))
                          }
                          idPrefix="edit-menu-summary-align"
                        />
                        <MenuSummaryBulletPicker
                          value={globalDetails.summaryBulletStyle}
                          onChange={(style) =>
                            setGlobalDetails((prev) => ({
                              ...prev,
                              summaryBulletStyle: style,
                            }))
                          }
                          idPrefix="edit-menu-summary-bullet"
                        />
                        <MenuSummaryPlusPicker
                          value={
                            globalDetails.summaryShowPlusBetweenCategories
                          }
                          onChange={(show) =>
                            setGlobalDetails((prev) => ({
                              ...prev,
                              summaryShowPlusBetweenCategories: show,
                            }))
                          }
                          idPrefix="edit-menu-summary-plus"
                        />
                      </MenuFormDisplaySection>

                      <MenuFormDisplaySection
                        step={3}
                        title={t("menuFormDisplaySectionTitles")}
                        hint={t("menuFormDisplaySectionTitlesHint")}
                      >
                        <MenuTitlesAlignPickers
                          categoryTitleAlign={globalDetails.categoryTitleAlign}
                          subCategoryTitleAlign={
                            globalDetails.subCategoryTitleAlign
                          }
                          productTitleAlign={globalDetails.productTitleAlign}
                          onCategoryTitleAlignChange={(v) =>
                            setGlobalDetails((prev) => ({
                              ...prev,
                              categoryTitleAlign: v,
                            }))
                          }
                          onSubCategoryTitleAlignChange={(v) =>
                            setGlobalDetails((prev) => ({
                              ...prev,
                              subCategoryTitleAlign: v,
                            }))
                          }
                          onProductTitleAlignChange={(v) =>
                            setGlobalDetails((prev) => ({
                              ...prev,
                              productTitleAlign: v,
                            }))
                          }
                          idPrefix="edit-menu-titles-align"
                        />
                      </MenuFormDisplaySection>

                      <MenuFormDisplaySection
                        step={4}
                        title={t("menuFormDisplaySectionStyle")}
                        hint={t("menuFormDisplaySectionStyleHint")}
                      >
                        <div className="menu-form-display-style-grid">
                          <ProductTitleIconStylePicker
                            value={globalDetails.productTitleIconStyle}
                            onChange={(style) =>
                              setGlobalDetails((prev) => ({
                                ...prev,
                                productTitleIconStyle: style,
                              }))
                            }
                            idPrefix="edit-menu"
                          />
                          <ProductCardSizePicker
                            value={globalDetails.productCardSize}
                            onChange={(size) =>
                              setGlobalDetails((prev) => ({
                                ...prev,
                                productCardSize: size,
                              }))
                            }
                            idPrefix="edit-menu"
                          />
                        </div>
                        <MenuTextFontPicker
                          productTextFont={globalDetails.productTextFont}
                          categoryTextFont={globalDetails.categoryTextFont}
                          subCategoryTextFont={globalDetails.subCategoryTextFont}
                          onProductTextFontChange={(font) =>
                            setGlobalDetails((prev) => ({
                              ...prev,
                              productTextFont: font,
                            }))
                          }
                          onCategoryTextFontChange={(font) =>
                            setGlobalDetails((prev) => ({
                              ...prev,
                              categoryTextFont: font,
                            }))
                          }
                          onSubCategoryTextFontChange={(font) =>
                            setGlobalDetails((prev) => ({
                              ...prev,
                              subCategoryTextFont: font,
                            }))
                          }
                          idPrefix="edit-menu"
                          compact
                        />
                      </MenuFormDisplaySection>
                    </div>
                  </Tab.Pane>
                  <Tab.Pane eventKey="manage">
                    {isInteractiveMenu ? (
                      <div className="menu-form-manage">
                        <MenuTree
                          menuId={id}
                          onEditMenu={() => setMainSectionTab("texts")}
                          onBackToMenus={() => navigate("/menu")}
                        />
                      </div>
                    ) : (
                      <p className="menu-form-shell__subtitle mb-0">{t("menuFormManageStatic")}</p>
                    )}
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>

              <div className="d-flex justify-content-end gap-2 pt-4 mt-2 border-top border-light">
                {mainSectionTab !== "manage" && isInteractiveMenu && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-4"
                    onClick={() => setMainSectionTab("manage")}
                  >
                    {t("manageMenu")}
                  </button>
                )}
                {mainSectionTab === "manage" ? (
                  <button
                    type="button"
                    className="btn btn-primary px-4"
                    onClick={() => navigate("/menu")}
                  >
                    {t("prodFormDone")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary px-4"
                    onClick={handleSubmit}
                  >
                    {t("saveChanges")}
                  </button>
                )}
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EditMenu;
